import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { CartProvider } from './context/CartContext';

import Header from './components/Header';
import Footer from './components/Footer';

import Home from './pages/Home';
import Products from './pages/Products';
import ProductDetail from './pages/ProductDetail';
import Login from './pages/Login';
import Terms from './pages/Terms';
import Register from './pages/Register';
import Cart from './pages/Cart';
import Checkout from './pages/Checkout';
import Orders from './pages/Orders';
import OrderDetail from './pages/OrderDetail';
import GuestOrderLookup from './pages/GuestOrderLookup';
import MyPage from './pages/MyPage';

import Notices from './pages/Notices';
import NoticeDetail from './pages/NoticeDetail';

import AdminDashboard from './pages/admin/AdminDashboard';
import AdminHome from './pages/admin/AdminHome';
import AdminProducts from './pages/admin/AdminProducts';
import AdminProductForm from './pages/admin/AdminProductForm';
import AdminCategories from './pages/admin/AdminCategories';
import AdminOrders from './pages/admin/AdminOrders';
import AdminUsers from './pages/admin/AdminUsers';
import AdminNotices from './pages/admin/AdminNotices';
import AdminNoticeForm from './pages/admin/AdminNoticeForm';
import AdminCrawler from './pages/admin/AdminCrawler';
import AdminOrderDetail from './pages/admin/AdminOrderDetail';
import AdminUserDetail from './pages/admin/AdminUserDetail';
import AdminFeatured from './pages/admin/AdminFeatured';
import AdminWeeklyBest from './pages/admin/AdminWeeklyBest';
import AdminPointsSettings from './pages/admin/AdminPointsSettings';
import AdminBanners from './pages/admin/AdminBanners';

// 페이지 이동 시 스크롤 최상단으로 이동
const ScrollToTop = () => {
  const { pathname, search } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname, search]);

  return null;
};

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return <div className="container loading">로딩 중...</div>;
  }

  return isAuthenticated ? children : <Navigate to="/login" />;
};

// Guest Route Component (로그인 상태면 메인으로 리다이렉트)
const GuestRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return <div className="container loading">로딩 중...</div>;
  }

  return isAuthenticated ? <Navigate to="/" /> : children;
};

// Admin Route Component
const AdminRoute = ({ children }) => {
  const { isAdmin, loading } = useAuth();

  if (loading) {
    return <div className="container loading">로딩 중...</div>;
  }

  return isAdmin ? children : <Navigate to="/" />;
};

function App() {
  return (
    <Router>
      <AuthProvider>
        <CartProvider>
          <ScrollToTop />
          <div className="app">
            <Header />
            <main style={{ minHeight: 'calc(100vh - 200px)' }}>
              <Routes>
                {/* Public Routes */}
                <Route path="/" element={<Home />} />
                <Route path="/products" element={<Products />} />
                <Route path="/products/:id" element={<ProductDetail />} />
                <Route path="/notices" element={<Notices />} />
                <Route path="/notices/:id" element={<NoticeDetail />} />
                <Route path="/login" element={<GuestRoute><Login /></GuestRoute>} />
                <Route path="/terms" element={<GuestRoute><Terms /></GuestRoute>} />
                <Route path="/register" element={<GuestRoute><Register /></GuestRoute>} />
                <Route path="/order-lookup" element={<GuestOrderLookup />} />

                {/* Cart & Checkout - 비회원도 접근 가능 */}
                <Route path="/cart" element={<Cart />} />
                <Route path="/checkout" element={<Checkout />} />

                {/* Protected Routes */}
                <Route 
                  path="/mypage" 
                  element={
                    <ProtectedRoute>
                      <MyPage />
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/orders" 
                  element={
                    <ProtectedRoute>
                      <Orders />
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/orders/:id" 
                  element={
                    <ProtectedRoute>
                      <OrderDetail />
                    </ProtectedRoute>
                  } 
                />

                {/* Admin Routes */}
                <Route 
                  path="/admin" 
                  element={
                    <AdminRoute>
                      <AdminDashboard />
                    </AdminRoute>
                  }
                >
                  <Route index element={<AdminHome />} />
                  <Route path="products" element={<AdminProducts />} />
                  <Route path="products/new" element={<AdminProductForm />} />
                  <Route path="products/:id/edit" element={<AdminProductForm />} />
                  <Route path="categories" element={<AdminCategories />} />
                  <Route path="orders" element={<AdminOrders />} />
                  <Route path="orders/:id" element={<AdminOrderDetail />} />
                  <Route path="users" element={<AdminUsers />} />
                  <Route path="users/:id" element={<AdminUserDetail />} />
                  <Route path="notices" element={<AdminNotices />} />
                  <Route path="notices/new" element={<AdminNoticeForm />} />
                  <Route path="notices/:id/edit" element={<AdminNoticeForm />} />
                  <Route path="featured" element={<AdminFeatured />} />
                  <Route path="weekly-best" element={<AdminWeeklyBest />} />
                  <Route path="points-settings" element={<AdminPointsSettings />} />
                  <Route path="banners" element={<AdminBanners />} />
                  <Route path="crawler" element={<AdminCrawler />} />
                </Route>

                {/* 404 */}
                <Route path="*" element={
                  <div className="container" style={{ textAlign: 'center', padding: '80px 20px' }}>
                    <h1 style={{ fontSize: '48px', marginBottom: '16px' }}>404</h1>
                    <p style={{ color: 'var(--secondary-color)', marginBottom: '24px' }}>
                      페이지를 찾을 수 없습니다.
                    </p>
                    <a href="/" className="btn btn-primary">홈으로 돌아가기</a>
                  </div>
                } />
              </Routes>
            </main>
            <Footer />
          </div>
        </CartProvider>
      </AuthProvider>
    </Router>
  );
}

export default App;

